import * as am4charts from "@amcharts/amcharts4/charts";
import {am4Settings} from "@/mixins/am4charts/am4Settings";

const isValidDate = dateObject => new Date(dateObject)
        .toString() !== 'Invalid Date';

export const DateAxis = class {
    constructor(start, end, timezone) {
        this.create(start, end, timezone)
    }

    getAm4Axis() {
        return this.am4Axis
    }

    setTimezone(timezone) {
        this.timezone = timezone
        this.am4Axis.dateFormatter.timezone = timezone

        let scrollAxis = this.getScrollAxisIfExists()
        if (!_.isNil(scrollAxis)) {
            scrollAxis.dateFormatter.timezone = timezone
        }
    }

    setSeries(series) {
        this.series = series
    }

    updateDateAxisInterval() {
        // Go through each series and keep record of a earliest first reading date and a latest last reading date
        // If all series have loaded restrict the start and end of the axis and shrink the interval of the dateAxis
        let numberVisibleSeries = 0
        let finishEarly = false
        let earliestReading = _.clone(this.end)
        let latestReading = _.clone(this.start)
        _.forEach(this.series, series => {
            if (!series.visible) {
                return
            }
            if (finishEarly || series.isLoadingData()) {
                finishEarly = true
                return
            }

            numberVisibleSeries++

            let earlyTime = series.getFirstReadingDate()
            if (earlyTime.getTime() < earliestReading.getTime()) {
                earliestReading = earlyTime
            }

            let lateTime = series.getLastReadingDate()
            if (lateTime.getTime() > latestReading.getTime()) {
                latestReading = lateTime
            }
        })

        if (finishEarly) {
            this.updateGapAndMinBulletDistance()
            return
        }

        this.updateTimeIntervals(
                numberVisibleSeries > 0 ? earliestReading : _.clone(this.start),
                numberVisibleSeries > 0 ? latestReading : _.clone(this.end),
                false)

        this.updateGapAndMinBulletDistance()
    }

    updateTimeIntervals(start = null, end = null, updateSeries = true) {
        this.start = _.isNil(start) ? this.start : start
        this.end = _.isNil(end) ? this.end : end

        // Update the Date Axis w/ the new min/max
        this.am4Axis.min = this.start.getTime()
        this.am4Axis.max = this.end.getTime()
        this.am4Axis.invalidate()

        // Update the Scroll Axis as well
        let scrollAxis = this.getScrollAxisIfExists()
        if (!_.isNil(scrollAxis)) {
            scrollAxis.min = _.clone(this.start)
            scrollAxis.max = _.clone(this.end)
            scrollAxis.invalidate()
        }

        this.am4Axis.dispatch('datarangechanged')

        if (!updateSeries) {
            return
        }

        _.forEach(this.series, series => {
            series.updateInterval(this.start, this.end)
        })
    }

    updateAllSeriesData() {

    }

    buildAndRender(chart) {
        // Give a buffer on either side of the minimum or maximum of the chart based on at least a percentage of the chart's height
        this.dispose()
        this.am4Chart = chart
        this.am4Axis = new am4charts.DateAxis()
        chart.xAxes.push(this.am4Axis)
        this.am4Axis.dateFormatter.timezone = this.timezone

        let scrollAxis = this.getScrollAxisIfExists()
        if (!_.isNil(scrollAxis)) {
            scrollAxis.dateFormatter.timezone = this.timezone
        }

        this.buildDateIntervals()
        this.buildDateAxisTooltip()
        this.buildDateAxisGrouping()
    }

    buildDateIntervals() {
        if (!_.isNil(am4Settings.dateFormatter)) {
            this.am4Axis.dateFormatter = am4Settings.dateFormatter
        }
        this.am4Axis.renderer.baseGrid.disabled = true
        this.am4Axis.renderer.labels.template.disabled = false
        this.am4Axis.renderer.grid.template.location = 0
        this.am4Axis.renderer.minGridDistance = 60
        this.am4Axis.gridIntervals.setAll([
            { timeUnit: 'second', count: 1, },
            { timeUnit: 'minute', count: 1, },
            { timeUnit: 'minute', count: 5, },
            { timeUnit: 'minute', count: 15, },
            { timeUnit: 'minute', count: 60, },
            { timeUnit: 'hour', count: 4, },
            { timeUnit: 'day', count: 1, },
            { timeUnit: 'day', count: 7, },
            { timeUnit: 'day', count: 14, },
            { timeUnit: 'month', count: 1, },
            { timeUnit: 'month', count: 3, },
            { timeUnit: 'month', count: 6, },
            { timeUnit: 'year', count: 1, },
        ])
    }

    buildDateAxisTooltip() {
        this.am4Axis.tooltipDateFormat = 'MM/dd/yyyy HH:mm:ss'
        // Figure out a way to have this not get created or run until data is rendered.
        this.am4Axis.adapter.add('getTooltipText', (tooltip, target) => {
            try {
                let series = null
                let data = null
                if (this.am4Chart.series.length > 0) {
                    series = this.am4Chart.series[0]
                }
                if (series !== null) {
                    data = series.tooltipDataItem
                }

                if (!data) {
                    return `Default (No Data): ${tooltip}`
                }
                let date = null
                if (data.groupDataItems && data.groupDataItems.length > 0) {
                    date = data.groupDataItems[0].dataContext.date
                }
                else {
                    date = data.dates.dateX
                }
                if (!date) {
                    return `Default (No Date): ${tooltip}`
                }
                return `${date.toLocaleDateString("en-US", {timeZone: this.timezone})}  ${date.toLocaleTimeString("en-US", {timeZone: this.timezone})}`
            }
            catch (e) {
                return tooltip
            }
        })
    }

    buildDateAxisGrouping() {
        this.am4Axis.groupData = true
        this.am4Axis.groupCount = 750
        let interval = this.getIntervals()

        this.am4Axis.groupInterval = interval[0]
        this.am4Axis.baseInterval = interval[0]

        this.am4Axis.events.on('datarangechanged', target => {
            let dateAxis = target.target
            if (dateAxis.groupData === false) {
                return
            }

            let currentInterval = dateAxis.groupInterval
            let start = dateAxis.positionToDate(dateAxis.startLocation)
            let end = dateAxis.positionToDate(dateAxis.endLocation)
            let interval = this.getIntervals(start, end, dateAxis.end - dateAxis.start)
            let intervalChanged = interval[0].timeUnit !== currentInterval.timeUnit
                    || interval[0].count !== currentInterval.count

            if (intervalChanged) {
                // Log interval changes for debugging
                if (!_.isNil(dateAxis.chart.cursor)) {
                    dateAxis.chart.cursor.disabled = true
                }

                dateAxis.groupInterval = interval[0]
                dateAxis.baseInterval = interval[0]

                dateAxis.series.each(series => {
                    series.dataGrouped = false
                    dateAxis.groupSeriesData(series)
                })
            }

            setTimeout(() => {
                this.updateGapAndMinBulletDistance()

                _.each(this.series, series => {
                    if (!series.visible) {
                        return
                    }

                    series.am4Series.dataGrouped = true
                })

                if (!_.isNil(dateAxis.chart.cursor)) {
                    dateAxis.chart.cursor.disabled = false
                }
            }, 100)
        })
    }

    axisInitialized() {
        return !_.isNil(this.am4Axis)
    }

    getScrollAxisIfExists() {
        let scrollbarExists =
               !_.isNil(this.am4Chart)
            && !_.isNil(this.am4Chart.scrollbarX)
            && !_.isNil(this.am4Chart.scrollbarX.scrollbarChart)
            && this.am4Chart.scrollbarX.scrollbarChart.xAxes.length > 0

        return scrollbarExists
                ? this.am4Chart.scrollbarX.scrollbarChart.xAxes.getIndex(0)
                : null
    }

    getIntervals(startDate = null, endDate = null, ratioUsed = 1) {
        startDate = _.isNil(startDate) || !isValidDate(startDate)
                ? this.start
                : startDate
        endDate = _.isNil(endDate) || !isValidDate(endDate)
                ? this.end
                : endDate
        let groupCount = am4Settings.GROUP_COUNT
        let delta = (endDate.getTime() - startDate.getTime()) / 1000
        let deltaRatio = delta / groupCount * ratioUsed

        let intervals = {
            1: { timeUnit: "second", count: 1 },
            5: { timeUnit: "second", count: 5 },
            15: { timeUnit: "second", count: 15 },
            30: { timeUnit: "second", count: 30 },
            60: { timeUnit: "minute", count: 1 },
            300: { timeUnit: "minute", count: 5 },
            600: { timeUnit: "minute", count: 10 },
            900: { timeUnit: "minute", count: 15 },
            1800: { timeUnit: "minute", count: 30 },
            3600: { timeUnit: "hour", count: 1 },
            14400: { timeUnit: "hour", count: 4 },
            86400: { timeUnit: "day", count: 1 },
            604800: { timeUnit: "week", count: 1 },
            1209600: { timeUnit: "week", count: 2 },
            2628000: { timeUnit: "month", count: 1 },
            10512000: { timeUnit: "month", count: 4 },
            15768000: { timeUnit: "month", count: 6 },
            31536000: { timeUnit: "year", count: 1 },
        }

        let desiredIntervalSeconds = null
        let intervalIndex = 0
        Object.keys(intervals).forEach((numSeconds, index) => {
            if (_.isNil(desiredIntervalSeconds) && deltaRatio < numSeconds) {
                desiredIntervalSeconds = numSeconds
                intervalIndex = index
            }
        })

        return [
            intervals[desiredIntervalSeconds],
        ]
    }

    updateGapAndMinBulletDistance() {
        // If the chart is null or is disposed get out.
        if (_.isNil(this.am4Chart) || this.am4Chart.isDisposed()) {
            return
        }

        // The gaps are assumed to be minutes.  We need to increase or decrease their time based on the data's baseInterval
        let scrollSeries = this.am4Chart.scrollbarX
        if (!_.isNil(scrollSeries) &&
                !_.isNil(scrollSeries.scrollbarChart) &&
                scrollSeries.scrollbarChart.series.length > 0) {
            scrollSeries = scrollSeries.scrollbarChart.series.getIndex(0)
        }

        let multiplier = 1
        let interval = this.am4Axis.baseInterval.timeUnit
        if (interval === 'second') {
            multiplier = 60
        }
        else if (interval === 'hour') {
            multiplier = 1.0 / 60
        }
        else if (interval === 'day') {
            multiplier = 1.0 / 60 / 24
        }

        let gapCount = am4Settings.useShortGaps ? am4Settings.SHORT_GAP_LENGTH * multiplier : am4Settings.LONG_GAP_LENGTH * multiplier

        // Update the Min Bullet Distance And Gap of each series on the date axis.
        _.each(this.series, series => {
            if (_.isNil(series) || !series.isAm4SeriesReady() || !series.visible) {
                return
            }

            series.getSeries().minBulletDistance = am4Settings.MIN_BULLET_DISTANCE
            series.getSeries().autoGapCount = gapCount
        })

        if (/*!isHeavyLoad &&*/ !_.isNil(scrollSeries)) {
            scrollSeries.minBulletDistance = 1000
            scrollSeries.autoGapCount = gapCount
        }
    }

    create(start, end, timezone) {
        this.series = []
        this.am4Chart = null
        this.timezone = timezone

        this.start = _.clone(start)
        this.end = _.clone(end)

        this.am4Axis = null
    }

    dispose() {
        if (!_.isNil(this.am4Axis)) {
            this.am4Axis.dispose()
        }
    }
}
